import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import { serviceEvaluations } from '../../services/service-evaluations';
import { EvaluationErrors, EvaluationLimits } from '../../util/evaluationContract';
import { evaluationErrorMessage, isValidEmail } from '../app-evaluation-start';
import { errorCodeOf, ManageHandlers, notifyDone, useManageAction } from './manageActions';
import { RecipientRow } from './RecipientTable';
import styles from './page-supplier-evaluations.module.scss';

type Props = ManageHandlers & { requestId: string; recipient: RecipientRow; onClose: () => void };

const FIELD_ERRORS: string[] = [
  EvaluationErrors.InvalidEmail,
  EvaluationErrors.DuplicateRecipient,
  EvaluationErrors.RecipientIsSupplierContact,
];

export const ChangeEmailModal: FC<Props> = ({ requestId, recipient, onClose, ...handlers }) => {
  const [t] = useTranslation('evaluations');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const { saving, run } = useManageAction(handlers, {
    closeOnStateChange: onClose,
    showsOwnRefusal: error => {
      const code = errorCodeOf(error);
      if (!FIELD_ERRORS.includes(code ?? '')) return false;
      setFieldError(evaluationErrorMessage(t, error));
      // A lost race on the row also answers DuplicateRecipient, so the row may have changed.
      if (code === EvaluationErrors.DuplicateRecipient) handlers.onStale();
      return true;
    },
  });
  const current = recipient.email ?? '';
  const [email, setEmail] = useState(current);
  const trimmed = email.trim();
  const canSave =
    isValidEmail(trimmed) &&
    trimmed.toLowerCase() !== current.toLowerCase() &&
    trimmed.length <= EvaluationLimits.EmailMaxLength &&
    !saving;

  const currentText = () => {
    if (!recipient.email) {
      return t('status.changeEmail.currentMember', 'Byter e-post för {{name}}.', {
        name: recipient.name,
      });
    }
    if (!recipient.name) {
      return t('status.changeEmail.currentNoName', 'Nuvarande adress: {{email}}', {
        email: recipient.email,
      });
    }
    return t('status.changeEmail.current', 'Nuvarande adress för {{name}}: {{email}}', {
      name: recipient.name,
      email: recipient.email,
    });
  };

  const change = (next: string) => {
    setEmail(next);
    setFieldError(null);
  };

  const save = () => {
    if (!canSave) return;
    run(
      () => serviceEvaluations.changeRecipientEmail(requestId, recipient._id, trimmed),
      result => {
        notifyDone(t, t('status.changeEmail.done', 'E-postadressen är bytt'), result.outcome);
        onClose();
      },
    );
  };

  return (
    <Modal
      show
      setShow={onClose}
      title={t('status.changeEmail.title', 'Byt e-post')}
      onCancelButtonClick={onClose}
      buttonInfo={{
        label: t('status.changeEmail.confirm', 'Byt och skicka'),
        action: save,
        disabled: !canSave,
        loaderShow: saving,
      }}
    >
      <div className={styles.modalBody}>
        <p className={`${styles.answerText} ${styles.muted}`}>{currentText()}</p>
        <div className={styles.fieldGroup}>
          <Field
            type="email"
            label={t('status.changeEmail.label', 'Ny e-postadress')}
            value={email}
            onChange={change}
          />
          {fieldError && (
            <p className={styles.fieldError} role="alert">
              {fieldError}
            </p>
          )}
          <p className={`${styles.small} ${styles.muted}`}>
            {t(
              'status.changeEmail.newLinkInfo',
              'En ny länk skickas till den nya adressen. Den gamla länken slutar fungera. Namnet tas bort från raden.',
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export const RevokeRecipientModal: FC<Props> = ({ requestId, recipient, onClose, ...handlers }) => {
  const [t] = useTranslation('evaluations');
  const { saving, run } = useManageAction(handlers, { closeOnStateChange: onClose });

  const revoke = () =>
    run(
      () => serviceEvaluations.removeRecipient(requestId, recipient._id),
      () => {
        notifyDone(t, t('status.revoke.done', 'Mottagaren är återkallad'), null);
        onClose();
      },
    );

  return (
    <Modal
      show
      setShow={onClose}
      title={t('status.revoke.title', 'Återkalla mottagare?')}
      onCancelButtonClick={onClose}
      buttonInfo={{
        label: t('status.revoke.confirm', 'Återkalla'),
        action: revoke,
        loaderShow: saving,
      }}
    >
      <div className={styles.modalBody}>
        <p className={styles.answerText}>
          {t('status.revoke.text', '{{name}} kan inte längre svara. Länken slutar fungera.', {
            name: recipient.name || recipient.email,
          })}
        </p>
      </div>
    </Modal>
  );
};
